export interface AppComponent {
  mount: () => void | Promise<void>;
  update?: () => void;
  destroy?: () => void;
}

type AppComponentConstructor = new (el: HTMLElement) => AppComponent;

type AppComponentLoader = () => Promise<AppComponentConstructor>;

interface MountableElement extends HTMLElement {
  componentMounted?: boolean;
}

// Component modules are loaded lazily so their chart libraries (echarts,
// lightweight-charts) are code-split into chunks that only chart pages download.
const appComponents: Record<string, AppComponentLoader> = {
  FundManagerIndexChart: () =>
    import("./fund-manager-index-chart").then((mod) => mod.FundManagerIndexChart),
  FundReturnChart: () => import("./fund-return-chart").then((mod) => mod.FundReturnChart),
  FundStockHistoryChart: () =>
    import("./fund-stock-history-chart").then((mod) => mod.FundStockHistoryChart),
  NetFlowChart: () => import("./net-flow-chart").then((mod) => mod.NetFlowChart),
  SignInForm: () => import("./sign-in-form").then((mod) => mod.SignInForm),
  StockHoldingsChart: () =>
    import("./stock-holdings-chart").then((mod) => mod.StockHoldingsChart),
  StockPriceChart: () => import("./stock-price-chart").then((mod) => mod.StockPriceChart),
  StatHistoryChart: () => import("./stat-history-chart").then((mod) => mod.StatHistoryChart),
  StockRatingsChart: () => import("./stock-ratings-chart").then((mod) => mod.StockRatingsChart),
  StockNewsSentimentChart: () =>
    import("./stock-news-sentiment-chart").then((mod) => mod.StockNewsSentimentChart),
};

// App components mounted this way are destroyed by LiveView when it navigates to a new page,
// and also when using the back button. We listed to the `phx:page-loading-stop` event to re-mount
// them. We add a JS flag `componentMounted` to the element to track if it's already mounted.
// Probably should find a better way to do this.

const mountComponent = async (el: MountableElement, loadComponent: AppComponentLoader) => {
  // Flag before the async load so a re-entrant init (e.g. another
  // `phx:page-loading-stop`) doesn't mount the component twice.
  el.componentMounted = true;

  const Component = await loadComponent();

  if (!el.isConnected) {
    // The element was removed while the module chunk loaded.
    el.componentMounted = undefined;
    return;
  }

  const component = new Component(el);

  el.addEventListener("updated", () => {
    component.update?.();
  });

  el.addEventListener("destroyed", () => {
    component.destroy?.();

    el.componentMounted = undefined;
  });

  void component.mount();
};

const doInitComponents = () => {
  for (const [key, loadComponent] of Object.entries(appComponents)) {
    document.querySelectorAll(`[data-app-component='${key}']`).forEach((rawEl) => {
      const el = rawEl as MountableElement;

      if (el.componentMounted) {
        return;
      }

      void mountComponent(el, loadComponent);
    });
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", doInitComponents);
} else {
  // DOM is already loaded
  doInitComponents();
}

window.addEventListener("phx:page-loading-stop", () => {
  doInitComponents();
});
